import type { BootstrapResponse, HistoryResponse, SendMessagePayload, SendMessageResponse, StreamEvent, Ticket } from '@dreamteam/chat-protocol';

export type TokenSource = string | (() => string | Promise<string>);

export interface ApiOptions {
  endpoint: string;
  appId: string;
  userToken?: TokenSource;
  /** Только для разработки: сервер должен разрешать devAuth. */
  devUser?: { id: string; name?: string };
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export class ApiClient {
  private base: string;
  private fetchImpl: typeof fetch;

  constructor(private opts: ApiOptions) {
    this.base = opts.endpoint.replace(/\/$/, '');
    // Сохраняем «чистый» fetch до патча рекордером (запросы чата не должны попадать в контекст).
    this.fetchImpl = window.fetch.bind(window);
  }

  setToken(t: TokenSource) {
    this.opts.userToken = t;
  }

  private async headers(): Promise<Record<string, string>> {
    const h: Record<string, string> = { 'X-DreamTeam-App': this.opts.appId };
    const t = this.opts.userToken;
    if (t) h.Authorization = `Bearer ${typeof t === 'function' ? await t() : t}`;
    else if (this.opts.devUser) {
      h['X-DreamTeam-Dev-User'] = this.opts.devUser.id;
      if (this.opts.devUser.name) h['X-DreamTeam-Dev-Name'] = encodeURIComponent(this.opts.devUser.name);
    }
    return h;
  }

  private async req<T>(method: string, path: string, body?: unknown | FormData): Promise<T> {
    const headers = await this.headers();
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
    const res = await this.fetchImpl(`${this.base}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
    if (!res.ok) {
      let msg = res.statusText;
      try {
        msg = (await res.json()).error ?? msg;
      } catch {
        /* noop */
      }
      throw new ApiError(res.status, msg);
    }
    return res.json() as Promise<T>;
  }

  bootstrap() {
    return this.req<BootstrapResponse>('GET', '/v1/widget/bootstrap');
  }

  history(before?: string, limit = 50) {
    const q = new URLSearchParams({ limit: String(limit) });
    if (before) q.set('before', before);
    return this.req<HistoryResponse>('GET', `/v1/widget/history?${q}`);
  }

  tickets() {
    return this.req<{ tickets: Ticket[] }>('GET', '/v1/widget/tickets');
  }

  sendMessage(payload: SendMessagePayload, files: Array<{ field: string; blob: Blob; filename: string }>) {
    if (!files.length) return this.req<SendMessageResponse>('POST', '/v1/widget/messages', payload);
    const form = new FormData();
    form.append('payload', JSON.stringify(payload));
    for (const f of files) form.append(f.field, f.blob, f.filename);
    return this.req<SendMessageResponse>('POST', '/v1/widget/messages', form);
  }

  submitTicket(id: string) {
    return this.req<{ ticket: Ticket }>('POST', `/v1/widget/tickets/${encodeURIComponent(id)}/submit`, {});
  }

  verdict(id: string, ok: boolean) {
    return this.req<{ ticket: Ticket }>('POST', `/v1/widget/tickets/${encodeURIComponent(id)}/verdict`, { ok });
  }

  /**
   * Поток событий (SSE) поверх fetch — чтобы передать заголовки авторизации.
   * Переподключается с экспоненциальной задержкой.
   */
  stream(onEvent: (e: StreamEvent) => void, onState?: (connected: boolean) => void): () => void {
    let stopped = false;
    let ctrl: AbortController | null = null;
    let delay = 1000;
    const loop = async () => {
      while (!stopped) {
        ctrl = new AbortController();
        try {
          const res = await this.fetchImpl(`${this.base}/v1/widget/stream`, { headers: await this.headers(), signal: ctrl.signal });
          if (!res.ok || !res.body) throw new ApiError(res.status, 'stream failed');
          onState?.(true);
          delay = 1000;
          const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
          let buf = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += value;
            let idx: number;
            while ((idx = buf.indexOf('\n\n')) >= 0) {
              const chunk = buf.slice(0, idx);
              buf = buf.slice(idx + 2);
              const data = chunk
                .split('\n')
                .filter((l) => l.startsWith('data:'))
                .map((l) => l.slice(5).trim())
                .join('\n');
              if (data) {
                try {
                  onEvent(JSON.parse(data));
                } catch {
                  /* ignore malformed */
                }
              }
            }
          }
        } catch {
          /* reconnect below */
        }
        onState?.(false);
        if (stopped) break;
        await new Promise((r) => setTimeout(r, delay));
        delay = Math.min(delay * 2, 30_000);
      }
    };
    void loop();
    return () => {
      stopped = true;
      ctrl?.abort();
    };
  }
}
